import { Form, Formik, useFormikContext } from 'formik';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { Button } from 'components/ui/Button';
import { usePanel } from 'hooks/usePanel';
import { Sizes } from 'enums/sizes';
import { createCollaboratorsForPm } from 'back-office/shared/components/collaborators/createCollaboratorsForPm';

import { BrokerForm } from '../../brokers-view/form/BrokerForm';
import { BrokerFormVariant } from '../../brokers-view/form/BrokerLayout';
import { BrokerFormValues } from '../../brokers-view/form/BrokerFormValues';
import { useBrokerForm } from '../../brokers-view/hooks/useBrokerForm';
import { CodeEntrepriseField } from '../components/CodeEntrepriseField';
import { createPlateforme } from '../api/plateformesApi';

interface PlateformeCourtierFormValues {
  code: string;
}

/**
 * Mode Administrée + cabinet à créer : le code entreprise est saisi ici, puis le
 * formulaire courtier standard s'ouvre en panneau — sa soumission crée la
 * plateforme ET le cabinet titulaire en une seule opération côté serveur
 * (pipeline courtier, PM CRT liée sans gouvernance) ; la plateforme prend la
 * raison sociale du cabinet comme nom. Les collaborateurs additionnels sont
 * créés dans la foulée, avec le récap habituel.
 */
export const PlateformeTitulaireCourtierForm: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <Formik<PlateformeCourtierFormValues>
    initialValues={{ code: '' }}
    onSubmit={() => undefined}
  >
    <Form className="flex flex-col gap-6">
      <CourtierFormBody onBack={onBack} />
    </Form>
  </Formik>
);

const CourtierFormBody: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const navigate = useNavigate();
  const { openPanel } = usePanel();
  const { buildCreateBrokerCommand } = useBrokerForm();
  const { values } = useFormikContext<PlateformeCourtierFormValues>();

  const openCourtierPanel = () => {
    const plateformeCode = values.code || null;

    openPanel(
      { title: 'Cabinet de courtage titulaire de la plateforme', size: Sizes['4xl'] },
      <BrokerForm
        variant={BrokerFormVariant.Plateforme}
        onSubmit={async (brokerValues: BrokerFormValues) => {
          // La plateforme n'existe pas encore : PlateformeIds est posé côté
          // serveur, et son nom est la raison sociale du cabinet.
          const response = await createPlateforme({
            name: brokerValues.companyName,
            code: plateformeCode,
            modeGestion: 'ADMINISTREE',
            courtier: buildCreateBrokerCommand(brokerValues, []),
          });

          if (!response.isSuccess) {
            // BrokerForm n'a pas de branche non-succès : sans ce toast, un échec
            // métier (nom dupliqué, code déjà pris…) laisserait le panneau muet.
            if (response.errorMessage) toast.error(response.errorMessage);
            return response;
          }

          const personneMoraleId = response.data?.personneMoraleId;
          const collaborators = brokerValues.collaborators ?? [];
          if (personneMoraleId && collaborators.length > 0) {
            return {
              ...response,
              collaboratorResults: await createCollaboratorsForPm(
                personneMoraleId,
                collaborators,
              ),
            };
          }

          return response;
        }}
        onSuccess={() => {
          toast.success('Plateforme administrée créée — habilitez maintenant ses produits.');
          navigate('/back-office/plateformes');
        }}
      />,
    );
  };

  return (
    <>
      <div className="rounded-xl bg-section p-6">
        <div className="grid grid-cols-2 gap-4">
          <CodeEntrepriseField name="code" autoGenerateIfEmpty />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          La plateforme prendra la raison sociale du cabinet comme nom. Le cabinet ne
          fera que distribuer : vous piloterez ses produits depuis les habilitations.
        </p>
      </div>

      <div className="flex items-center justify-between">
        <Button outline type="button" onClick={onBack}>
          <ArrowLeft data-slot="icon" />
          Retour
        </Button>
        <Button color="primary" type="button" onClick={openCourtierPanel}>
          Renseigner le cabinet et créer
          <ArrowRight data-slot="icon" />
        </Button>
      </div>
    </>
  );
};
